import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login, clearToken } from '../api';

const CUSTOMERS = [
  { key: 'tyson', name: 'Tyson Foods', color: '#FED34C', textColor: '#333' },
  { key: 'seaboard', name: 'Seaboard Foods', color: '#003876' },
  { key: 'jbs', name: 'JBS USA', color: '#1B1464' },
];

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('customer');
  const matchedCustomer = preselected
    ? CUSTOMERS.find((c) => c.key === preselected.toLowerCase()) ||
      (preselected.toLowerCase() === 'admin' ? { key: 'admin', name: 'RMG Admin', color: '#1f2937' } : null)
    : null;

  const [mode, setMode] = useState(matchedCustomer ? 'login' : 'select');
  const [selectedCustomer, setSelectedCustomer] = useState(matchedCustomer);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Clear any stale auth on mount
  React.useEffect(() => { clearToken(); }, []);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setMode('login');
    setPassword('');
    setError('');
  };

  const handleAdminLogin = () => {
    setSelectedCustomer({ key: 'admin', name: 'RMG Admin', color: '#1f2937' });
    setMode('login');
    setPassword('');
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const authData = await login(selectedCustomer.key, password);

      if (authData.is_admin) {
        // Admin goes to first customer dashboard
        navigate('/dashboard/tyson');
      } else {
        // Customer goes directly to their dashboard
        navigate(`/dashboard/${authData.customer}`);
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">
          RMG Container Reports
        </h1>

        {mode === 'select' ? (
          <>
            <p className="text-gray-500 text-center mb-6">
              Select your account to sign in
            </p>
            <div className="space-y-3">
              {CUSTOMERS.map((c) => (
                <button
                  key={c.key}
                  onClick={() => handleSelectCustomer(c)}
                  className="w-full py-3 px-4 rounded-lg text-lg font-semibold hover:opacity-90 transition"
                  style={{ backgroundColor: c.color, color: c.textColor || '#fff' }}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleAdminLogin}
                className="w-full bg-gray-800 text-white py-3 px-4 rounded-lg text-lg font-semibold hover:bg-gray-700 transition"
              >
                RMG Admin
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-500 text-center mb-6">
              Sign in to{' '}
              <span className="font-semibold text-gray-700">
                {selectedCustomer.name}
              </span>
            </p>

            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent text-lg"
                  autoFocus
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-lg text-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
                style={{ backgroundColor: selectedCustomer.color, color: selectedCustomer.textColor || '#fff' }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {!matchedCustomer && (
              <button
                onClick={() => setMode('select')}
                className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm py-2 transition"
              >
                Back to account selection
              </button>
            )}
          </>
        )}
      </div>
      <p className="mt-4 text-sm text-gray-400">Rail Modal Group</p>
    </div>
  );
}
